import CurrencyInputField from 'components/_UI/CurrencyInputField';
import Fieldset from 'components/_UI/Fieldset';
import React, { useEffect, useState } from 'react'
import colors from 'theme/colors';
import styled from 'styled-components';
import { useEvent } from 'context/events/Event';
import Switch from 'components/_UI/Switch';
import InfoTooltip from 'components/_UI/InfoTooltip';
import eventTypes from 'theme/eventTypes';
import { AlertCircle, ArrowUp, ArrowDown, ChevronDown, ChevronUp, Copy, DollarSign, Hash, Tag, Trash2, ToggleRight, Calendar, FileText, Layers, MessageSquareText } from 'lucide-react';
import TextArea from 'components/_UI/TextArea';
import FormBatches from './FormBatches';
import currency from 'helpers/currency';
import useEventServiceFee from 'hooks/useEventServiceFee';
import SalePeriodForm from './SalePeriodForm';
import { SaleTypes } from 'modules/_common/SaleTypes';
import breakpoints from 'theme/breakpoints';
import { quantityFloorError } from './batchStock';
import {
    BUYER_MESSAGE_LINK_LABEL_MAX_LENGTH,
    BUYER_MESSAGE_MAX_LENGTH,
    isBuyerMessageLinkValid,
} from './buyerMessage';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

export default function DefaultForm({
    ticketIndex,
    selected,
    isUnique,
    onTicketClick = () => false,
    onDeleteTicket = () => false,
    onDuplicateTicket = () => false,
    onMoveUp = () => false,
    onMoveDown = () => false,
    canMoveUp = false,
    canMoveDown = false,
    ticket,
    setTicket,
    setDeletedBatches = () => false,
    hasTicketError = false,
    onRequestAbsorbedChange = () => false,
    absorbedResetKey = 0,
}) {
    const { event } = useEvent();
    // Evento gratuito trava todo ingresso em R$ 0,00. Já `is_free` é decisão por
    // ingresso: evento pago também pode ter cortesia, convite ou lista.
    const isEventFree = event?.classification.id === Number(eventTypes.free.id);
    const isFreeTicket = isEventFree || ticket.is_free === true;
    // Single source of truth lives in the parent's createdTickets; the switch is prop-driven
    // so toggling one ticket keeps every ticket's switch in sync.
    const absorbedServiceFee = ticket.absorbed_service_fee ?? false
    const requiresDocument = ticket.requires_document ?? false
    const [serviceFee, setServiceFee] = useState('')
    const [serviceFeePix, setServiceFeePix] = useState('')
    const [netIncome, setNetIncome] = useState('')
    const [netIncomePix, setNetIncomePix] = useState('')
    const [nameError, setNameError] = useState('')
    const [descriptionError, setDescriptionError] = useState('')
    const [priceError, setPriceError] = useState('')
    const [quantityError, setQuantityError] = useState('')
    const [batchNameError, setBatchNameError] = useState('')
    const [hiddenCodeError, setHiddenCodeError] = useState('')
    const [hasHiddenCode, setHasHiddenCode] = useState(!!ticket.hidden_code)
    const [hasBuyerMessage, setHasBuyerMessage] = useState(!!(ticket.buyer_message || ticket.buyer_message_link_url))
    const buyerMessageLength = ticket.buyer_message?.length || 0
    const buyerMessageLinkLabelLength = ticket.buyer_message_link_label?.length || 0
    const isBuyerMessageLinkInvalid = !isBuyerMessageLinkValid(ticket.buyer_message_link_url)
    const [hasSalePeriod, setHasSalePeriod] = useState(false)
    const [salePeriod, setSalePeriod] = useState({
        sale_start: null,
        sale_end: null
    })
    const [reverseMode, setReverseMode] = useState(false)
    const [reverseValue, setReverseValue] = useState('')

    // A taxa é a do evento, não uma constante: quem resolve isso é o hook.
    const { ready: feeReady, calculateFee, calculateFeePix, calculatePriceFromNet } = useEventServiceFee()

    function changeActive(value) {
        setTicket(tickets => {
            const newTickets = [...tickets]
            newTickets[ticketIndex].is_active = value
            return newTickets
        })
    }

    function changeName(value) {
        setTicket(tickets => {
            const newTickets = [...tickets]
            newTickets[ticketIndex].name = value
            return newTickets
        })

        if (!value) {
            setNameError('O campo é obrigatório')
            return
        }
        if (value.length > 30) {
            setNameError('O nome deve ter menos de 30 caracteres')
            return
        }
        setNameError('')
    }

    function changeQuantity(value) {
        const parsedValue = !value ? '' : value.replace(/\D/g, '');
        setTicket(tickets => {
            const newTickets = [...tickets]
            newTickets[ticketIndex].quantity = parsedValue
            return newTickets
        })

        if (!parsedValue) {
            setQuantityError('A quantidade é obrigatória')
            return
        }
        if (Number(parsedValue) <= 0) {
            setQuantityError('A quantidade deve ser acima de 0')
            return
        }

        // Reduzir abaixo do que já saiu é recusado pelo servidor; avisar aqui
        // evita o produtor descobrir só depois de salvar.
        const floorError = quantityFloorError(ticket.committed, parsedValue)

        if (floorError) {
            setQuantityError(floorError)
            return
        }

        setQuantityError('')
    }

    function changeDescription(value) {
        setTicket(tickets => {
            const newTickets = [...tickets]
            newTickets[ticketIndex].description = value
            return newTickets
        })

        if (value.length > 80) {
            setDescriptionError('A descrição deve ter menos de 80 caracteres')
            return
        }
        setDescriptionError('')
    }

    // Ingresso gratuito é decisão do produtor, não do tipo do evento. O lote
    // acompanha: um ingresso a R$ 0,00 com lote 2 a R$ 20 seria contradição.
    function toggleFreeTicket(newIsFree) {
        setReverseMode(false)
        setReverseValue('')
        setServiceFee(currency.format(0))
        setServiceFeePix(currency.format(0))
        setNetIncome(currency.format(0))
        setNetIncomePix(currency.format(0))

        setTicket(tickets => {
            const newTickets = [...tickets]
            const current = newTickets[ticketIndex]

            newTickets[ticketIndex] = {
                ...current,
                is_free: newIsFree,
                price: newIsFree ? '0,00' : '',
                floatPrice: 0,
                batches: current.batches.map(batch => (
                    newIsFree
                        ? { ...batch, price: '0,00', floatPrice: 0 }
                        : batch
                ))
            }

            return newTickets
        })

        setPriceError(newIsFree ? '' : 'O preço do ingresso é obrigatório')
    }

    function changePrice(value, name, values) {
        const fee = calculateFee(values.float)
        const feePix = calculateFeePix(values.float)
        setServiceFee(isFreeTicket ? currency.format(0) : currency.format(fee))
        setServiceFeePix(isFreeTicket ? currency.format(0) : currency.format(feePix))
        setNetIncome(
            isFreeTicket
                ? currency.format(0)
                : (values.float - fee) > 0
                    ? currency.format(values.float - fee)
                    : currency.format(0)
        )
        setNetIncomePix(
            isFreeTicket
                ? currency.format(0)
                : (values.float - feePix) > 0
                    ? currency.format(values.float - feePix)
                    : currency.format(0)
        )

        setTicket(tickets => {
            const newTickets = [...tickets]
            newTickets[ticketIndex].price = value
            newTickets[ticketIndex].floatPrice = values.float
            return newTickets
        })

        if (!value) {
            setPriceError('O preço do ingresso é obrigatório')
            return
        }
        if (!isFreeTicket && values.float < 5) {
            setPriceError('Mínimo de R$5,00. Para R$ 0,00, ative "Ingresso gratuito".')
            return
        }
        setPriceError('')
    }

    function changeReversePrice(value, name, values) {
        const desiredNet = values.float
        const calculatedPrice = calculatePriceFromNet(desiredNet)
        const fee = calculateFee(calculatedPrice)
        const feePix = calculateFeePix(calculatedPrice)

        setReverseValue(value)
        setServiceFee(currency.format(fee))
        setServiceFeePix(currency.format(feePix))
        setNetIncome(currency.format(desiredNet))
        setNetIncomePix(
            (calculatedPrice - feePix) > 0
                ? currency.format(calculatedPrice - feePix)
                : currency.format(0)
        )

        setTicket(tickets => {
            const newTickets = [...tickets]
            newTickets[ticketIndex].price = currency.format(calculatedPrice).replace('R$\u00a0', '')
            newTickets[ticketIndex].floatPrice = calculatedPrice
            return newTickets
        })

        if (!value) {
            setPriceError('O valor desejado é obrigatório')
            return
        }
        setPriceError('')
    }

    function enterReverseMode() {
        const price = ticket.floatPrice
        if (price > 0) {
            const fee = calculateFee(price)
            const net = price - fee > 0 ? price - fee : 0
            setReverseValue(currency.format(net).replace('R$\u00a0', ''))
        }
        setReverseMode(true)
    }

    function toggleSalePeriod(newHasSalePeriod) {
        setHasSalePeriod(newHasSalePeriod)
        const saleType = newHasSalePeriod ? SaleTypes.PERIOD : SaleTypes.BATCH
        setTicket(tickets => {
            const newTickets = [...tickets]
            newTickets[ticketIndex].sale_type = saleType
            return newTickets
        })
    }

    function changeSalePeriod({ sale_start, sale_end }, ticketIndex) {
        setTicket(tickets => {
            const newTickets = [...tickets]
            newTickets[ticketIndex].sale_start = sale_start
            newTickets[ticketIndex].sale_end = sale_end
            return newTickets
        })
    }

    function changeHiddenCode(value) {
        setTicket(tickets => {
            const newTickets = [...tickets]
            newTickets[ticketIndex].hidden_code = value || null
            return newTickets
        })

        if (value && value.length > 30) {
            setHiddenCodeError('O código deve ter menos de 30 caracteres')
            return
        }
        setHiddenCodeError('')
    }

    function toggleHiddenCode(newHasHiddenCode) {
        setHasHiddenCode(newHasHiddenCode)
        if (!newHasHiddenCode) {
            setHiddenCodeError('')
            setTicket(tickets => {
                const newTickets = [...tickets]
                newTickets[ticketIndex].hidden_code = null
                return newTickets
            })
        }
    }

    function changeBuyerMessageField(field, value) {
        setTicket(tickets => {
            const newTickets = [...tickets]
            newTickets[ticketIndex][field] = value
            return newTickets
        })
    }

    function toggleBuyerMessage(newHasBuyerMessage) {
        setHasBuyerMessage(newHasBuyerMessage)
        if (!newHasBuyerMessage) {
            setTicket(tickets => {
                const newTickets = [...tickets]
                newTickets[ticketIndex].buyer_message = ''
                newTickets[ticketIndex].buyer_message_link_url = ''
                newTickets[ticketIndex].buyer_message_link_label = ''
                return newTickets
            })
        }
    }

    function toggleRequiresDocument(value) {
        setTicket(tickets => {
            const newTickets = [...tickets]
            newTickets[ticketIndex].requires_document = value
            return newTickets
        })
    }

    function changeBatchName(newBatchName) {
        setTicket(tickets => {
            const newTickets = [...tickets]
            newTickets[ticketIndex].batchName = newBatchName
            return newTickets
        })

        if (!newBatchName) {
            setBatchNameError('O nome do lote é obrigatório.')
            return
        }
        if (newBatchName.length >= 30) {
            setBatchNameError('O nome deve ter menos de 30 caracteres')
            return
        }
        setBatchNameError('')
    }

    function onHeaderClick() {
        onTicketClick()
    }

    /* A taxa chega depois da tela: enquanto a resposta não voltava, este cálculo
       rodava só na montagem e o resumo ficava com a taxa padrão para sempre.
       Depende de `calculateFee`, que muda de identidade quando a taxa do evento
       aterrissa — é isso que faz o valor se corrigir sozinho. */
    useEffect(() => {
        const price = ticket.floatPrice
        const fee = calculateFee(price)
        const feePix = calculateFeePix(price)

        setServiceFee(isFreeTicket ? currency.format(0) : currency.format(fee))
        setServiceFeePix(isFreeTicket ? currency.format(0) : currency.format(feePix))
        setNetIncome(
            isFreeTicket
                ? currency.format(0)
                : (price - fee) > 0
                    ? currency.format(price - fee)
                    : currency.format(0)
        )
        setNetIncomePix(
            isFreeTicket
                ? currency.format(0)
                : (price - feePix) > 0
                    ? currency.format(price - feePix)
                    : currency.format(0)
        )
    }, [ticket.floatPrice, isFreeTicket, calculateFee, calculateFeePix])

    useEffect(() => {
        setHasSalePeriod(ticket.sale_type === SaleTypes.PERIOD)

        if (ticket.sale_type === undefined) {
            setTicket(tickets => {
                const newTickets = [...tickets]
                newTickets[ticketIndex].sale_type = SaleTypes.BATCH
                return newTickets
            })
        }

        if (ticket.sale_type === SaleTypes.PERIOD) {
            setSalePeriod({
                sale_start: ticket.sale_start,
                sale_end: ticket.sale_end
            })
        }
    }, [])

    /**
     * O que o produtor recebe por ingresso.
     *
     * Só depende da taxa quando ele a absorve — se quem paga é o cliente, o
     * líquido é o preço cheio e não há por que esperar a resposta da API.
     */
    function netIncomeLabel() {
        if (isFreeTicket) {
            return currency.format(0)
        }

        if (!absorbedServiceFee) {
            return currency.format(ticket.floatPrice)
        }

        if (!feeReady) {
            return '—'
        }

        return `${netIncome} no cartão · ${netIncomePix} no Pix`
    }

    function getTotalRevenue() {
        const firstBatchPrice = ticket.floatPrice || 0
        const firstBatchQty = Number(ticket.quantity) || 0
        const firstBatchFee = calculateFee(firstBatchPrice)
        const firstBatchFeePix = calculateFeePix(firstBatchPrice)
        const firstBatchGross = firstBatchPrice * firstBatchQty
        const firstBatchNet = (firstBatchPrice - firstBatchFee) > 0 ? (firstBatchPrice - firstBatchFee) * firstBatchQty : 0
        const firstBatchNetPix = (firstBatchPrice - firstBatchFeePix) > 0 ? (firstBatchPrice - firstBatchFeePix) * firstBatchQty : 0
        const firstBatchTotalFees = firstBatchFee * firstBatchQty
        const firstBatchTotalFeesPix = firstBatchFeePix * firstBatchQty

        let totalTickets = firstBatchQty
        let grossRevenue = firstBatchGross
        let totalFees = firstBatchTotalFees
        let totalFeesPix = firstBatchTotalFeesPix
        let netRevenue = firstBatchNet
        let netRevenuePix = firstBatchNetPix

        for (const batch of ticket.batches) {
            const bPrice = batch.floatPrice || 0
            const bQty = Number(batch.quantity) || 0
            const bFee = calculateFee(bPrice)
            const bFeePix = calculateFeePix(bPrice)
            totalTickets += bQty
            grossRevenue += bPrice * bQty
            totalFees += bFee * bQty
            totalFeesPix += bFeePix * bQty
            netRevenue += (bPrice - bFee) > 0 ? (bPrice - bFee) * bQty : 0
            netRevenuePix += (bPrice - bFeePix) > 0 ? (bPrice - bFeePix) * bQty : 0
        }

        return { totalTickets, grossRevenue, totalFees, totalFeesPix, netRevenue, netRevenuePix }
    }

    return (
        <Container active={selected}>
            <TicketHeader onClick={onHeaderClick} active={selected}>
                <HeaderLeft>
                    <TicketBadge active={selected} hasError={hasTicketError && !selected}>
                        {hasTicketError && !selected
                            ? <AlertCircle size={14} />
                            : <Tag size={14} />
                        }
                    </TicketBadge>
                    <HeaderInfo>
                        <TicketName active={selected}>
                            {ticket.name || 'Ingresso sem nome'}
                        </TicketName>
                        {!selected && (
                            <HeaderMeta>
                                {(isFreeTicket || ticket.floatPrice > 0) && (
                                    <span>{isFreeTicket ? 'Gratuito' : currency.format(ticket.floatPrice)}</span>
                                )}
                                {(isFreeTicket || ticket.floatPrice > 0) && ticket.quantity && <MetaDot />}
                                {ticket.quantity && <span>{ticket.quantity} un.</span>}
                                {ticket.batches?.length > 0 && (
                                    <>
                                        <MetaDot />
                                        <MetaBadge><Layers size={10} />{ticket.batches.length + 1} lotes</MetaBadge>
                                    </>
                                )}
                                {hasSalePeriod && (
                                    <>
                                        <MetaDot />
                                        <MetaBadge><Calendar size={10} />Período</MetaBadge>
                                    </>
                                )}
                                {ticket.description && (
                                    <>
                                        <MetaDot />
                                        <MetaBadge><FileText size={10} />Desc.</MetaBadge>
                                    </>
                                )}
                                {hasBuyerMessage && (
                                    <>
                                        <MetaDot />
                                        <MetaBadge><MessageSquareText size={10} />Recado</MetaBadge>
                                    </>
                                )}
                            </HeaderMeta>
                        )}
                    </HeaderInfo>
                    {ticket.is_active && <ActiveBadge>Ativo</ActiveBadge>}
                </HeaderLeft>

                <HeaderActions>
                    {canMoveUp && (
                        <ActionIcon
                            data-tooltip="Mover ingresso para cima na ordem de exibição"
                            onClick={(e) => {
                                e.stopPropagation();
                                onMoveUp();
                            }}
                        >
                            <ArrowUp size={15} />
                        </ActionIcon>
                    )}
                    {canMoveDown && (
                        <ActionIcon
                            data-tooltip="Mover ingresso para baixo na ordem de exibição"
                            onClick={(e) => {
                                e.stopPropagation();
                                onMoveDown();
                            }}
                        >
                            <ArrowDown size={15} />
                        </ActionIcon>
                    )}
                    {!isUnique && (
                        <ActionIcon
                            data-tooltip="Duplicar este ingresso com as mesmas configurações"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDuplicateTicket();
                            }}
                        >
                            <Copy size={15} />
                        </ActionIcon>
                    )}
                    {!isUnique && (
                        <ActionIcon
                            danger
                            data-tooltip="Excluir este ingresso permanentemente"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDeleteTicket();
                            }}
                        >
                            <Trash2 size={15} />
                        </ActionIcon>
                    )}
                    <ChevronIcon>
                        {selected ? (
                            <ChevronUp size={18} color={panelAccent(colors.occasionalPurple)} />
                        ) : (
                            <ChevronDown size={18} color={colors.night} />
                        )}
                    </ChevronIcon>
                </HeaderActions>
            </TicketHeader>

            <Content active={selected}>
                <ModernFormArea>
                    <SectionLabel>
                        <Tag size={14} color={panelAccent(colors.occasionalPurple)} />
                        Informações do ingresso
                    </SectionLabel>

                    <FieldGrid columns={2}>
                        <FieldWrapper>
                            <Fieldset
                                layout='squired'
                                label='Nome do ingresso'
                                placeholder='Ingresso meia entrada, camarote, etc'
                                value={ticket.name}
                                onChangeValue={changeName}
                            />
                            <FieldFooter>
                                {nameError
                                    ? <FieldError><AlertCircle size={13} />{nameError}</FieldError>
                                    : <CharCount warn={ticket.name?.length > 25}>{ticket.name?.length || 0}/30</CharCount>
                                }
                            </FieldFooter>
                        </FieldWrapper>

                        <FieldWrapper>
                            <Fieldset
                                layout='squired'
                                label='Descrição (Opcional)'
                                placeholder='Ao comprar o ingresso ganhe um copo de brinde...'
                                value={ticket.description}
                                onChangeValue={changeDescription}
                            />
                            <FieldFooter>
                                {descriptionError
                                    ? <FieldError><AlertCircle size={13} />{descriptionError}</FieldError>
                                    : <CharCount warn={ticket.description?.length > 65}>{ticket.description?.length || 0}/80</CharCount>
                                }
                            </FieldFooter>
                        </FieldWrapper>
                    </FieldGrid>

                    <SectionLabel>
                        <DollarSign size={14} color={panelAccent(colors.occasionalPurple)} />
                        Preço e quantidade
                    </SectionLabel>

                    <FieldGrid columns={2}>
                        <FieldWrapper>
                            {!reverseMode ? (
                                <>
                                    <CurrencyInputField
                                        layout='squired'
                                        placeholder='R$ 50,00'
                                        value={ticket.price}
                                        disabled={isFreeTicket}
                                        label='Valor do ingresso'
                                        onChange={changePrice}
                                    />
                                    {priceError && (
                                        <FieldError><AlertCircle size={13} />{priceError}</FieldError>
                                    )}
                                    {isFreeTicket && (
                                        <FreeHint>
                                            Ingresso gratuito: o comprador não paga nada e não há taxa de serviço.
                                        </FreeHint>
                                    )}
                                    {isFreeTicket && !isEventFree && (
                                        <ReverseToggle onClick={() => toggleFreeTicket(false)}>
                                            Desativar ingresso gratuito para definir um preço
                                        </ReverseToggle>
                                    )}
                                    {!isFreeTicket && (
                                        <ReverseToggle onClick={enterReverseMode}>
                                            Quero definir quanto vou receber
                                        </ReverseToggle>
                                    )}
                                </>
                            ) : (
                                <>
                                    <CurrencyInputField
                                        layout='squired'
                                        placeholder='R$ 45,00'
                                        label='Quanto você quer receber?'
                                        value={reverseValue}
                                        onChange={changeReversePrice}
                                    />
                                    {ticket.floatPrice > 0 && (
                                        <ReverseResult>
                                            Preço sugerido: <strong>{currency.format(ticket.floatPrice)}</strong>
                                        </ReverseResult>
                                    )}
                                    <ReverseToggle onClick={() => setReverseMode(false)}>
                                        Voltar para preço direto
                                    </ReverseToggle>
                                </>
                            )}
                        </FieldWrapper>

                        <FieldWrapper>
                            <Fieldset
                                layout='squired'
                                label='Quantidade'
                                placeholder='100'
                                value={ticket.quantity}
                                syncProp={true}
                                onChangeValue={changeQuantity}
                            />
                            {quantityError && (
                                <FieldError><AlertCircle size={13} />{quantityError}</FieldError>
                            )}
                        </FieldWrapper>
                    </FieldGrid>
                </ModernFormArea>

                <BottomRow>
                    <InfoCard>
                        <InfoCardTitle>
                            <DollarSign size={13} />
                            Resumo financeiro
                        </InfoCardTitle>
                        <InfoRow>
                            <InfoLabel>Preço do ingresso</InfoLabel>
                            <InfoValue positive>
                                {isFreeTicket ? 'Gratuito' : currency.format(ticket.floatPrice)}
                            </InfoValue>
                        </InfoRow>
                        <InfoRow>
                            <InfoLabel>Taxa no cartão</InfoLabel>
                            {/* Sem a taxa do evento em mãos não há número honesto a
                                mostrar: o produtor precifica em cima desta linha. */}
                            <InfoValue>{feeReady ? serviceFee : '—'}</InfoValue>
                        </InfoRow>
                        <InfoRow>
                            <InfoLabel>Taxa no Pix</InfoLabel>
                            <InfoValue>{feeReady ? serviceFeePix : '—'}</InfoValue>
                        </InfoRow>
                        <InfoRow>
                            <InfoLabel>Pago por</InfoLabel>
                            <InfoValue>{isFreeTicket ? '—' : absorbedServiceFee ? 'Você (produtor)' : 'Cliente'}</InfoValue>
                        </InfoRow>
                        <InfoDivider />
                        <InfoRow>
                            <InfoLabel bold>Você irá receber</InfoLabel>
                            <InfoValue positive bold>{netIncomeLabel()}</InfoValue>
                        </InfoRow>
                    </InfoCard>

                    <SettingsCard>
                        <InfoCardTitle>
                            <ToggleRight size={13} />
                            Configurações
                        </InfoCardTitle>
                        <SwitchRow>
                            <SwitchControl>
                                <Switch
                                    checked={ticket.is_active}
                                    onChange={changeActive}
                                />
                            </SwitchControl>
                            <SwitchLabel>
                                Ingresso ativo
                                <InfoTooltip
                                    bgColor={panelAccent(colors.occasionalPurple)}
                                    text="Se ativo, o ingresso estará disponível para compra."
                                />
                            </SwitchLabel>
                        </SwitchRow>
                        {/* Em evento gratuito todo ingresso já é grátis: o botão
                            só existe onde é escolha, que é no evento pago. */}
                        {!isEventFree && (
                            <SwitchRow>
                                <SwitchControl>
                                    <Switch
                                        checked={isFreeTicket}
                                        onChange={toggleFreeTicket}
                                    />
                                </SwitchControl>
                                <SwitchLabel>
                                    Ingresso gratuito
                                    <InfoTooltip
                                        bgColor={panelAccent(colors.occasionalPurple)}
                                        text="Zera o preço deste ingresso e de todos os seus lotes. Vale mesmo em evento pago — para cortesia, convite ou lista."
                                    />
                                </SwitchLabel>
                            </SwitchRow>
                        )}
                        <SwitchRow>
                            <SwitchControl>
                                <Switch
                                    checked={hasSalePeriod}
                                    onChange={toggleSalePeriod}
                                />
                            </SwitchControl>
                            <SwitchLabel>
                                Período de vendas
                                <InfoTooltip
                                    bgColor={panelAccent(colors.occasionalPurple)}
                                    text="Configurar uma data de início e fim para as vendas deste ingresso."
                                />
                            </SwitchLabel>
                        </SwitchRow>
                        <SwitchRow>
                            <SwitchControl>
                                <Switch
                                    checked={hasHiddenCode}
                                    onChange={toggleHiddenCode}
                                />
                            </SwitchControl>
                            <SwitchLabel>
                                Ingresso oculto
                                <InfoTooltip
                                    bgColor={panelAccent(colors.occasionalPurple)}
                                    text="Esconde o ingresso da página pública. Compradores precisam do código para acessá-lo."
                                />
                            </SwitchLabel>
                        </SwitchRow>
                        <SwitchRow>
                            <SwitchControl>
                                <Switch
                                    checked={requiresDocument}
                                    onChange={toggleRequiresDocument}
                                />
                            </SwitchControl>
                            <SwitchLabel>
                                Pedir CPF de cada participante
                                <InfoTooltip
                                    bgColor={panelAccent(colors.occasionalPurple)}
                                    text="Quando ativo, cada participante precisa informar o próprio CPF na compra. Útil para gerar nota fiscal individual."
                                />
                            </SwitchLabel>
                        </SwitchRow>
                        <SwitchRow>
                            <SwitchControl>
                                <Switch
                                    checked={hasBuyerMessage}
                                    onChange={toggleBuyerMessage}
                                />
                            </SwitchControl>
                            <SwitchLabel>
                                Recado para quem comprar
                                <InfoTooltip
                                    bgColor={panelAccent(colors.occasionalPurple)}
                                    text="Texto e link opcional enviados no e-mail de pedido aprovado. Não aparece na página do evento."
                                />
                            </SwitchLabel>
                        </SwitchRow>
                        {!isFreeTicket && (
                            <SwitchRow>
                                <SwitchControl>
                                    <Switch
                                        key={absorbedResetKey}
                                        checked={absorbedServiceFee}
                                        onChange={onRequestAbsorbedChange}
                                    />
                                </SwitchControl>
                                <SwitchLabel>
                                    Absorver taxa de serviço
                                    <InfoTooltip
                                        bgColor={panelAccent(colors.occasionalPurple)}
                                        text="Quando ativo, você paga a taxa de serviço e o cliente paga apenas o valor do ingresso. Quando inativo, a taxa é repassada ao cliente."
                                    />
                                </SwitchLabel>
                            </SwitchRow>
                        )}
                        {hasHiddenCode && (
                            <HiddenCodeField>
                                <Fieldset
                                    layout='squired'
                                    label='Código oculto'
                                    placeholder='Ex: VIP2026'
                                    value={ticket.hidden_code || ''}
                                    syncProp={true}
                                    onChangeValue={changeHiddenCode}
                                />
                                <FieldFooter>
                                    {hiddenCodeError
                                        ? <FieldError><AlertCircle size={13} />{hiddenCodeError}</FieldError>
                                        : <CharCount warn={(ticket.hidden_code?.length || 0) > 25}>{ticket.hidden_code?.length || 0}/30</CharCount>
                                    }
                                </FieldFooter>
                            </HiddenCodeField>
                        )}
                    </SettingsCard>
                </BottomRow>

                {hasSalePeriod && (
                    <SalePeriodWrapper>
                        <SectionLabel>
                            <Calendar size={14} color={panelAccent(colors.occasionalPurple)} />
                            Período de vendas
                        </SectionLabel>
                        <SalePeriodForm
                            salePeriod={salePeriod}
                            onChangeSalePeriod={(salePeriod) => changeSalePeriod(salePeriod, ticketIndex)}
                        />
                    </SalePeriodWrapper>
                )}

                {hasBuyerMessage && (
                    <BuyerMessageSection>
                        <SectionLabel>
                            <MessageSquareText size={14} color={panelAccent(colors.occasionalPurple)} />
                            Recado para quem comprar
                        </SectionLabel>
                        <BuyerMessageHint>
                            Vai no e-mail de pedido aprovado, só para quem comprou este ingresso. Se você editar depois, só os próximos pedidos recebem o texto novo.
                        </BuyerMessageHint>
                        <FieldWrapper>
                            <TextArea
                                squired
                                rows={4}
                                maxLength={BUYER_MESSAGE_MAX_LENGTH}
                                label='Mensagem'
                                placeholder='Ex: Entre no grupo do evento para receber as novidades e o cronograma.'
                                defaultValue={ticket.buyer_message || ''}
                                onChangeValue={value => changeBuyerMessageField('buyer_message', value)}
                            />
                            <FieldFooter>
                                <CharCount warn={buyerMessageLength > BUYER_MESSAGE_MAX_LENGTH - 100}>
                                    {buyerMessageLength}/{BUYER_MESSAGE_MAX_LENGTH}
                                </CharCount>
                            </FieldFooter>
                        </FieldWrapper>
                        <FieldGrid columns={2}>
                            <FieldWrapper>
                                <Fieldset
                                    layout='squired'
                                    label='Link (opcional)'
                                    placeholder='https://chat.whatsapp.com/...'
                                    value={ticket.buyer_message_link_url || ''}
                                    syncProp={true}
                                    onChangeValue={value => changeBuyerMessageField('buyer_message_link_url', value)}
                                />
                                {isBuyerMessageLinkInvalid && (
                                    <FieldError><AlertCircle size={13} />Link inválido</FieldError>
                                )}
                            </FieldWrapper>
                            {ticket.buyer_message_link_url && (
                                <FieldWrapper>
                                    <Fieldset
                                        layout='squired'
                                        label='Texto do botão (opcional)'
                                        placeholder='Acessar link'
                                        value={ticket.buyer_message_link_label || ''}
                                        syncProp={true}
                                        maxLength={BUYER_MESSAGE_LINK_LABEL_MAX_LENGTH}
                                        onChangeValue={value => changeBuyerMessageField('buyer_message_link_label', value)}
                                    />
                                    <FieldFooter>
                                        <CharCount warn={buyerMessageLinkLabelLength > BUYER_MESSAGE_LINK_LABEL_MAX_LENGTH - 10}>
                                            {buyerMessageLinkLabelLength}/{BUYER_MESSAGE_LINK_LABEL_MAX_LENGTH}
                                        </CharCount>
                                    </FieldFooter>
                                </FieldWrapper>
                            )}
                        </FieldGrid>
                    </BuyerMessageSection>
                )}

                {/* Batch name visible outside advanced */}
                <BatchNameSection>
                    <SectionLabel>
                        <Hash size={14} color={panelAccent(colors.occasionalPurple)} />
                        Nome do primeiro lote
                    </SectionLabel>
                    <ModernFormArea>
                        <FieldGrid columns={1}>
                            <FieldWrapper>
                                <Fieldset
                                    layout='squired'
                                    label='Nome do lote (Lote 1)'
                                    placeholder='Lote 1'
                                    value={ticket.batchName}
                                    syncProp={true}
                                    onChangeValue={changeBatchName}
                                />
                                {batchNameError && (
                                    <FieldError><AlertCircle size={13} />{batchNameError}</FieldError>
                                )}
                            </FieldWrapper>
                        </FieldGrid>
                    </ModernFormArea>
                </BatchNameSection>

                {/* Revenue preview */}
                {ticket.batches?.length > 0 && !isFreeTicket && (() => {
                    const { totalTickets, grossRevenue, totalFees, totalFeesPix, netRevenue, netRevenuePix } = getTotalRevenue()
                    return (
                        <TotalRevenueCard>
                            <InfoCardTitle>
                                <DollarSign size={13} />
                                Projeção de receita total
                            </InfoCardTitle>
                            <InfoRow>
                                <InfoLabel>Total de ingressos</InfoLabel>
                                <InfoValue>{totalTickets} un.</InfoValue>
                            </InfoRow>
                            <InfoRow>
                                <InfoLabel>Receita bruta</InfoLabel>
                                <InfoValue positive>{currency.format(grossRevenue)}</InfoValue>
                            </InfoRow>
                            <InfoRow>
                                <InfoLabel>Total de taxas (cartão)</InfoLabel>
                                <InfoValue>{absorbedServiceFee ? currency.format(totalFees) : 'Pagas pelo cliente'}</InfoValue>
                            </InfoRow>
                            <InfoRow>
                                <InfoLabel>Total de taxas (Pix)</InfoLabel>
                                <InfoValue>{absorbedServiceFee ? currency.format(totalFeesPix) : 'Pagas pelo cliente'}</InfoValue>
                            </InfoRow>
                            <InfoDivider />
                            <InfoRow>
                                <InfoLabel bold>Receita líquida projetada</InfoLabel>
                                <InfoValue positive bold>
                                    {absorbedServiceFee
                                        ? `${currency.format(netRevenue)} no cartão · ${currency.format(netRevenuePix)} no Pix`
                                        : currency.format(grossRevenue)}
                                </InfoValue>
                            </InfoRow>
                        </TotalRevenueCard>
                    )
                })()}

                {!isEventFree && (
                    <BatchesSection>
                        <SectionLabel>
                            <Hash size={14} color={panelAccent(colors.occasionalPurple)} />
                            Configuração de lotes
                            <InfoTooltip
                                text="Configurações de lotes permitem definir diferentes preços para diferentes quantidades de ingressos."
                            />
                        </SectionLabel>

                        <FormBatches
                            batches={ticket.batches}
                            setTicket={setTicket}
                            ticketIndex={ticketIndex}
                            hasSalePeriod={hasSalePeriod}
                            setDeletedBatches={setDeletedBatches}
                            absorbedServiceFee={absorbedServiceFee}
                            isFreeTicket={isFreeTicket}
                        />
                    </BatchesSection>
                )}

            </Content>
        </Container>
    )
}


const Container = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'active',
})`
    border: 1.5px solid ${({ active }) => active ? panelAccentAlpha(colors.occasionalPurple, '44') : colors.night + '22'};
    border-radius: 12px;
    margin-bottom: 12px;
    transition: all 0.2s ease;
    background: #fff;
    overflow: visible;

    ${({ active }) => active && `
        box-shadow: 0 2px 12px ${panelAccentAlpha(colors.occasionalPurple, '12')};
    `};
`;

const TicketHeader = styled.div.withConfig({
    shouldForwardProp: (prop) => !['active'].includes(prop),
})`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 16px;
    cursor: pointer;
    transition: background 0.15s ease;
    border-bottom: 1.5px solid transparent;

    ${({ active }) => active && `
        border-bottom-color: ${colors.night}11;
        background: ${panelAccentAlpha(colors.occasionalPurple, '05')};
    `};

    &:hover {
        background: ${colors.night}08;
    }
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    flex: 1;
`;

const HeaderInfo = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

const TicketBadge = styled.div.withConfig({
    shouldForwardProp: (prop) => !['active', 'hasError'].includes(prop),
})`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: ${({ active, hasError }) =>
        hasError ? colors.danger + '15'
        : active ? panelAccentAlpha(colors.occasionalPurple, '15')
        : colors.night + '11'};
    color: ${({ active, hasError }) =>
        hasError ? colors.danger
        : active ? panelAccent(colors.occasionalPurple)
        : colors.night};
    transition: all 0.2s ease;
`;

const TicketName = styled.span.withConfig({
    shouldForwardProp: (prop) => !['active'].includes(prop),
})`
    font-size: 15px;
    font-weight: 600;
    color: ${({ active }) => active ? panelAccent(colors.occasionalPurple) : colors.nightDark};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: color 0.2s ease;
`;

const HeaderMeta = styled.span`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: ${colors.night};
    margin-top: 2px;
`;

const MetaDot = styled.span`
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: ${colors.night}66;
`;

const ActiveBadge = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: ${colors.success};
    background: ${colors.success}12;
    padding: 2px 8px;
    border-radius: 10px;
    flex-shrink: 0;
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
    margin-left: 8px;
`;

const ActionIcon = styled.span.withConfig({
    shouldForwardProp: (prop) => !['danger'].includes(prop),
})`
    width: 30px;
    height: 30px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: ${({ danger }) => danger ? colors.danger : colors.night};
    transition: all 0.15s ease;
    position: relative;

    &:hover {
        background: ${({ danger }) => danger ? colors.danger + '10' : colors.night + '10'};
        color: ${({ danger }) => danger ? colors.dangerDark : colors.nightDark};
    }

    &[data-tooltip]:hover::after {
        content: attr(data-tooltip);
        position: absolute;
        bottom: calc(100% + 8px);
        right: 0;
        background: rgba(0, 0, 0, 0.85);
        color: #fff;
        font-size: 11px;
        font-weight: 500;
        padding: 6px 10px;
        border-radius: 6px;
        white-space: nowrap;
        z-index: 10;
        pointer-events: none;
    }

    &[data-tooltip]:hover::before {
        content: '';
        position: absolute;
        bottom: calc(100% + 3px);
        right: 8px;
        border-width: 5px;
        border-style: solid;
        border-color: rgba(0, 0, 0, 0.85) transparent transparent transparent;
        z-index: 10;
        pointer-events: none;
    }
`;

const ChevronIcon = styled.span`
    width: 30px;
    height: 30px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const Content = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'active',
})`
    height: 0;
    opacity: 0;
    visibility: hidden;
    overflow: hidden;
    transition: all 0.25s ease;

    ${({ active }) => active && `
        height: auto;
        opacity: 1;
        visibility: visible;
        overflow: visible;
        padding: 16px;
    `};
`;

const ModernFormArea = styled.div`
    input:not([class*="css-"]):not([id*="react-select"]) {
        background: ${colors.night}08 !important;
        border: 1.5px solid ${colors.night}22 !important;
        border-radius: 10px !important;
        padding: 12px 14px !important;
        font-size: 14px !important;
        color: ${colors.nightDark} !important;
        transition: all 0.15s ease !important;

        &::placeholder {
            color: ${colors.night}88 !important;
        }

        &:focus {
            border-color: ${panelAccent(colors.occasionalPurple)} !important;
            box-shadow: 0 0 0 3px ${panelAccentAlpha(colors.occasionalPurple, '18')} !important;
            background: #fff !important;
        }
    }

    label {
        font-size: 13px !important;
        font-weight: 600 !important;
        color: ${colors.nightDark} !important;
        margin-bottom: 4px !important;
    }
`;

const SectionLabel = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin-bottom: 12px;
    margin-top: 4px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
`;

const FieldGrid = styled.div`
    display: grid;
    gap: 12px;
    grid-template-columns: 1fr;
    margin-bottom: 20px;

    @media screen and (min-width: ${breakpoints.md}) {
        grid-template-columns: repeat(${({ columns }) => columns || 1}, 1fr);
    }
`;

const FieldWrapper = styled.div`
    width: 100%;
`;

const FieldFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 2px;
`;

const CharCount = styled.span.withConfig({
    shouldForwardProp: (prop) => !['warn'].includes(prop),
})`
    font-size: 11px;
    font-weight: 500;
    color: ${({ warn }) => warn ? colors.occasionalOrange : colors.night + '88'};
    transition: color 0.15s ease;
`;

const FieldError = styled.span`
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    font-weight: 500;
    color: ${colors.danger};
    padding: 4px 8px;
    background: ${colors.danger}0A;
    border-radius: 6px;
    line-height: 1.4;
    width: 100%;
`;

const FreeHint = styled.p`
    margin: 6px 0 0;
    font-size: 11px;
    line-height: 1.4;
    color: ${colors.night};
`;

const ReverseToggle = styled.button`
    background: none;
    border: none;
    padding: 0;
    margin-top: 6px;
    font-size: 12px;
    font-weight: 600;
    color: ${panelAccent(colors.occasionalPurple)};
    cursor: pointer;
    transition: opacity 0.15s ease;

    &:hover {
        opacity: 0.7;
    }
`;

const ReverseResult = styled.div`
    font-size: 12px;
    color: ${colors.night};
    margin-top: 6px;
    padding: 6px 10px;
    background: ${colors.success}0A;
    border-radius: 6px;
    border: 1px solid ${colors.success}22;

    strong {
        color: ${colors.success};
        font-weight: 600;
    }
`;

const BottomRow = styled.div`
    display: grid;
    gap: 12px;
    grid-template-columns: 1fr;

    @media screen and (min-width: ${breakpoints.md}) {
        grid-template-columns: 1fr 1fr;
    }
`;

const InfoCard = styled.div`
    background: ${colors.night}06;
    border: 1px solid ${colors.night}11;
    border-radius: 10px;
    padding: 14px 16px;
`;

const InfoCardTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: ${colors.night};
    text-transform: uppercase;
    letter-spacing: 0.3px;
    margin-bottom: 12px;
`;

const InfoRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 3px 0;
`;

const InfoLabel = styled.span`
    font-size: 13px;
    color: ${colors.night};
    ${({ bold }) => bold && `
        font-weight: 600;
        color: ${colors.nightDark};
    `};
`;

const InfoValue = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${({ positive }) => positive ? colors.success : colors.night};
    ${({ bold }) => bold && `
        font-size: 15px;
        font-weight: 600;
    `};
`;

const InfoDivider = styled.div`
    height: 1px;
    background: ${colors.night}15;
    margin: 8px 0;
`;

const SettingsCard = styled.div`
    background: ${colors.night}06;
    border: 1px solid ${colors.night}11;
    border-radius: 10px;
    padding: 14px 16px;
`;

const SwitchRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 0;
`;

const SwitchControl = styled.div`
    flex-shrink: 0;
    width: auto;

    & > div {
        width: auto;
    }
`;

const SwitchLabel = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    font-weight: 500;
    color: ${colors.nightDark};
`;

const SalePeriodWrapper = styled.div`
    margin-top: 16px;
`;

const HiddenCodeField = styled.div`
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid ${colors.night}11;

    input:not([class*="css-"]):not([id*="react-select"]) {
        background: #fff !important;
        border: 1.5px solid ${colors.night}22 !important;
        border-radius: 10px !important;
        padding: 10px 12px !important;
        font-size: 13px !important;
        color: ${colors.nightDark} !important;

        &:focus {
            border-color: ${panelAccent(colors.occasionalPurple)} !important;
            box-shadow: 0 0 0 3px ${panelAccentAlpha(colors.occasionalPurple, '18')} !important;
        }
    }

    label {
        font-size: 12px !important;
        font-weight: 600 !important;
        color: ${colors.nightDark} !important;
        margin-bottom: 4px !important;
    }
`;

const MetaBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 11px;
    font-weight: 600;
    color: ${panelAccent(colors.occasionalPurple)};
    background: ${panelAccentAlpha(colors.occasionalPurple, '10')};
    padding: 1px 6px;
    border-radius: 8px;
`;

const BuyerMessageSection = styled.div`
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid ${colors.night}11;
    display: flex;
    flex-direction: column;
    gap: 12px;

    textarea {
        background: #fff;
        font-size: 13px;
        font-weight: 400;
        line-height: 1.5;
    }
`;

const BuyerMessageHint = styled.p`
    margin: -4px 0 0;
    font-size: 12px;
    line-height: 1.4;
    color: ${colors.night}AA;
`;

const BatchNameSection = styled.div`
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid ${colors.night}11;
`;

const TotalRevenueCard = styled.div`
    margin-top: 16px;
    background: ${panelAccentAlpha(colors.occasionalPurple, '06')};
    border: 1px solid ${panelAccentAlpha(colors.occasionalPurple, '18')};
    border-radius: 10px;
    padding: 14px 16px;
`;

const BatchesSection = styled.div`
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid ${colors.night}11;
`;
